import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { useEffect } from 'react';
import i18n from './i18n/i18n';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { growthbook } from './lib/growthbookClient';

import Home from './Home';
import Hentai from './Hentai';
import Porn from './Porn';
import AuthModal from './components/shared/AuthModal';
import Step5 from './components/steps/Step5';
import useGTM from './hooks/useGtm';
import { DesignProvider } from './contexts/DesignContext';
import { GrowthBookProvider as CustomGrowthBookProvider } from './contexts/GrowthBookContext';
import { STORAGE_KEYS } from './constants';
import { isAnnualPlan } from './utils/storage';

const supported = [
  'en',
  'fr',
  'de',
  'es',
  'it',
  'ja',
  'ar',
  'id',
  'ko',
  'nl',
  'pl',
  'pt',
  'ru',
  'tr',
  'zh',
];
//dds

const browserLang = (() => {
  const lang = navigator.language.split('-')[0];
  return supported.includes(lang) ? lang : 'en';
})();

function SummaryPage() {
  const navigate = useNavigate();
  const selections = (() => {
    try {
      return JSON.parse(
        localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}',
      );
    } catch {
      return {};
    }
  })();
  return <Step5 selections={selections} onAnswer={() => navigate('/signup')} />;
}

function SignupModalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const style =
    new URLSearchParams(location.search).get('style') || 'Realistic';
  return (
    <AuthModal
      isOpen={true}
      onClose={() => navigate(-1)}
      selectedStyle={style}
    />
  );
}

function NewDesignRedirect() {
  const { lang } = useParams();
  const location = useLocation();

  useEffect(() => {
    const targetPath = lang
      ? `/${lang}/ndg${location.pathname.replace(`/${lang}/new-design`, '')}`
      : `/ndg${location.pathname.replace('/new-design', '')}`;
    window.location.replace(targetPath);
  }, [lang, location.pathname]);

  return null;
}

function LanguageWrapper() {
  useGTM();

  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage(browserLang);
      return;
    }

    if (supported.includes(lang)) {
      i18n.changeLanguage(lang);
      if (
        lang === browserLang &&
        location.pathname.startsWith(`/${browserLang}`)
      ) {
        const cleanPath =
          location.pathname.replace(new RegExp(`^/${browserLang}`), '') || '/';
        navigate(cleanPath, { replace: true });
      }
      if (
        lang === 'en' &&
        browserLang === 'en' &&
        location.pathname.startsWith('/en')
      ) {
        const cleanPath = location.pathname.replace(/^\/en/, '') || '/';
        navigate(cleanPath, { replace: true });
      }
    } else {
      navigate('/', { replace: true });
    }
  }, [lang, location.pathname, navigate]);

  return (
    <>
      <Outlet />
    </>
  );
}

function AnnualRouteWrapper() {
  return <LanguageWrapper />;
}

export default function Root() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const canRedirect = urlParams.get('redirect') !== 'false';

    const inProgress = localStorage.getItem('googleAuthInProgress');
    const startTime = parseInt(localStorage.getItem('googleAuthStartTime'), 10);

    if (inProgress && startTime) {
      const timeElapsed = Date.now() - startTime;

      if (timeElapsed < 3 * 60 * 1000) {
        localStorage.setItem('googleAuthCancelled', 'true');
      } else {
        localStorage.setItem('isCompleted', true);
        localStorage.removeItem('googleAuthCancelled');
      }

      localStorage.removeItem('googleAuthInProgress');
      localStorage.removeItem('googleAuthStartTime');
    }

    // paywall_popup Google return lands here with ?auth_token — stay on the
    // lander (createAccount reopens the popup) instead of redirecting to /paywall.
    const isPaywallPopupReturn = urlParams.has('auth_token');

    const isCompleted = localStorage.getItem('isCompleted');
    if (isCompleted && canRedirect && !isPaywallPopupReturn) {
      const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
      const isAnnualRoute = isAnnualPlan();
      // /12 route forces annual; otherwise honor the GB
      // createsexfriends-post-signup-redirect flag persisted at signup time.
      const annualByFlag =
        !isAnnualRoute &&
        localStorage.getItem('postSignupRedirect') === 'annual';
      const annualParam = isAnnualRoute || annualByFlag ? '&plans=annual' : '';
      window.location.href = `${SWIPEY_BASE_URL}/paywall?from_landing=true${annualParam}`;
    }
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <CustomGrowthBookProvider>
        <Router basename={process.env.PUBLIC_URL}>
          <DesignProvider>
            <Routes>
              {/* Root routes */}
              <Route path="/" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="create-ai-hentai" element={<Hentai />} />
                <Route path="create-ai-porn" element={<Porn />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              {/* /12 - annual plan route */}
              <Route path="/12" element={<AnnualRouteWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="create-ai-hentai" element={<Hentai />} />
                <Route path="create-ai-porn" element={<Porn />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              {/* Halloween */}
              <Route path="/halloween" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
              </Route>

              {/* Valentines */}
              <Route path="/valentines" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
              </Route>

              {/* New Design Redirect */}
              <Route path="/new-design" element={<NewDesignRedirect />} />
              <Route path="/new-design/*" element={<NewDesignRedirect />} />

              {/* NDG */}
              <Route path="/ndg" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="create-ai-hentai" element={<Hentai />} />
                <Route path="create-ai-porn" element={<Porn />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              {/* Language routes */}
              <Route path="/:lang" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="create-ai-hentai" element={<Hentai />} />
                <Route path="create-ai-porn" element={<Porn />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              {/* Language + Halloween */}
              <Route path="/:lang/halloween" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
              </Route>

              {/* Language + Valentines */}
              <Route path="/:lang/valentines" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
              </Route>

              {/* Language + New Design Redirect */}
              <Route path="/:lang/new-design" element={<NewDesignRedirect />} />
              <Route
                path="/:lang/new-design/*"
                element={<NewDesignRedirect />}
              />

              {/* Language + NDG */}
              <Route path="/:lang/ndg" element={<LanguageWrapper />}>
                <Route index element={<Home />} />
                <Route path="signup" element={<SignupModalPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="create-ai-hentai" element={<Hentai />} />
                <Route path="create-ai-porn" element={<Porn />} />
                <Route path="newaccount" element={<Home />} />
                <Route path="pw" element={<Home />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </DesignProvider>
        </Router>
      </CustomGrowthBookProvider>
    </GrowthBookProvider>
  );
}
